import { useEffect, useState } from 'react';
import type { MeResponse } from '../api/auth';
import { useAuth } from '../auth/AuthContext';

type CacheEntry<T> = { value: T; updatedAt: number };
type QueryState<T> = {
  owner: MeResponse | null;
  key: string;
  entry: CacheEntry<T> | null;
  error: unknown | null;
};

const cache = new WeakMap<MeResponse, Map<string, CacheEntry<unknown>>>();

function readEntry<T>(owner: MeResponse | null, key: string): CacheEntry<T> | null {
  return owner ? (cache.get(owner)?.get(key) as CacheEntry<T> | undefined) ?? null : null;
}

function saveEntry<T>(owner: MeResponse, key: string, value: T): CacheEntry<T> {
  let entries = cache.get(owner);
  if (!entries) {
    entries = new Map();
    cache.set(owner, entries);
  }
  const entry = { value, updatedAt: Date.now() };
  entries.set(key, entry);
  return entry;
}

export function useRondoPageData<T>(key: string, load: () => Promise<T>, refreshMs = 0) {
  const { me } = useAuth();
  const [state, setState] = useState<QueryState<T>>(() => ({ owner: me, key, entry: readEntry<T>(me, key), error: null }));
  const current = state.owner === me && state.key === key
    ? state
    : { owner: me, key, entry: readEntry<T>(me, key), error: null };

  useEffect(() => {
    if (!me) return;
    let active = true;
    const refresh = () => {
      load()
        .then((value) => {
          if (!active) return;
          setState({ owner: me, key, entry: saveEntry(me, key, value), error: null });
        })
        .catch((error: unknown) => {
          if (!active) return;
          setState({ owner: me, key, entry: readEntry<T>(me, key), error });
        });
    };
    refresh();
    const timer = refreshMs > 0 ? window.setInterval(refresh, refreshMs) : null;
    return () => {
      active = false;
      if (timer !== null) window.clearInterval(timer);
    };
  }, [key, load, me, refreshMs]);

  return {
    data: current.entry?.value ?? null,
    updatedAt: current.entry?.updatedAt ?? null,
    loading: current.entry === null && current.error === null,
    error: current.error,
  };
}
